import { CoverageTypeEnum } from "@platform/types";

import { DocumentLibraryCoverageType } from "./coverage-type";

import type {
  DocumentErrorExcludedCode,
  GlobalErrorCode,
  ErrorKey,
  DocumentType,
} from "./types";

export const DOCUMENT_ERROR_EXCLUDED_MESSAGES: Record<
  DocumentErrorExcludedCode,
  string
> = {
  "ERR-G-001": "Document is too large for automated processing.",
  "ERR-G-002": "Unsupported file format. PDF, DOCX, or XLSX required.",
  "ERR-P-001": "Document is blank and cannot be processed.",
  "ERR-P-002": "Document coverage does not match assigned coverage.",
  "ERR-P-004": "Document content is corrupted and unreadable.",
  "ERR-RATE-MAP": "Rate documents could not be matched to detected classes.",
  "ERR-DATE": "Plan effective date cannot be identified.",
  "WAR-CERT-DENTAL": "Certificate was excluded from processing.",
  "WAR-CERT-OTHER": "Summary was excluded from processing.",
  "WAR-EXCL-REASONS": "Non-coverage related document.",
  "WAR-NODATA": "No relevant data found.",
  "WAR-RATES": "Rates not found.",
  "WAR-EXCL-REASONS-2": "Unable to process.",
  "WAR-SUM": "Superseded by Higher-Priority Document.",
  "ERR-FIELD": "Group field-level details could not be processed.",
  "ERR-PAGE": "Unable to determine the page count for this document.",
  "ERR-CLASS": "Class information not detected.",
  "ERR-CARRIER": "Carrier not identified.",
  "ERR-AIL":
    "Some document details are unavailable because AI processing is temporarily offline. Broker-provided and system-generated information remains accessible.",
  "ERR-AIE":
    "We couldn't populate RFP details using AI at this time. Please review and enter the information manually.",
  "ERR-DOWN":
    "AI Document Parsing is not available now, please try again later.",
  "EXC-001": "Non-coverage related document.",
  "EXC-002": "Summary was prioritized.",
  "EXC-003": "Certification was prioritized.",
  "EXC-004": "Document with duplicate data.",
  "EXC-005": "Coverage type is not supported for automatic extraction.",
  "EXC-NOT-EXTRACTABLE":
    "Document type is not supported for automatic extraction.",
  "EXC-RENEWAL": "Renewal rates document.",
};

export const GLOBAL_ERROR_MESSAGES: Record<GlobalErrorCode, string> = {
  "ERR-G-003": "AI agent unavailable.",
  "ERR-DOWN":
    "AI Document Parsing is not available now, please try again later.",
  "ERR-POLICY":
    "Carrier policy details are available, the provided documents were excluded from processing.",
  "ERR-HISTORICAL": "Plan effective date cannot be identified.",
  "ERR-G-004":
    "Unable to retrieve documents from the document source. Please try again later.",
  "ERR-SAVEMETADATA":
    "Document metadata could not be saved. Processing may be incomplete.",
  "ERR-BACKEND":
    "Our AI processing service is temporarily unavailable, so documents and RFP data cannot be auto-filled at the moment. You can continue by manually entering the required information.",
  "ERR-AIE":
    "We couldn't populate RFP details using AI at this time. Please review and enter the information manually.",
};

export const AI_SERVICE_UNAVAILABLE_GLOBAL_ERROR_CODES =
  new Set<GlobalErrorCode>(["ERR-G-003", "ERR-DOWN", "ERR-BACKEND", "ERR-AIE"]);

// Icon Colors
export const DocLibraryColors = {
  errorIcon: "#B71C1C",
  successIcon: "#4CAF50",
  processingIcon: "brandSupporting.medium",
  excludedIcon: "action.active",
  totalDocumentsIcon: "text.primary",
  infoIcon: "#707070",
} as const;

export const DEFAULT_ERROR_MESSAGE = "An unexpected error occurred.";

export const DEFAULT_DOCUMENT_ERROR_MESSAGE = "Unable to process document.";

export const OPEN_ERROR_MESSAGE =
  "Unable to open the document right now. Please try again.";
export const VIEW_ERROR_MESSAGE =
  "Unable to view the document right now. Please try again.";
export const DOWNLOAD_ERROR_MESSAGE =
  "Unable to download the document right now. Please try again.";
export const REMOVE_ERROR_MESSAGE =
  "Unable to remove the document right now. Please try again.";
export const RENAME_ERROR_MESSAGE =
  "Unable to rename the document right now. Please try again.";
export const PDF_PREVIEW_ONLY_ERROR_MESSAGE =
  "Only PDF documents can be previewed. Please download the file to view it.";
export const PDF_RENAME_ONLY_ERROR_MESSAGE =
  "Only PDF documents can be renamed.";

export const ERROR_KEYS: ErrorKey[] = ["general", "upload", "review", "docs"];

export const COVERAGE_TYPE_TO_API_NAME: Record<
  DocumentLibraryCoverageType,
  string
> = {
  [CoverageTypeEnum.DENTAL]: "Dental",
  [CoverageTypeEnum.DENTAL_PPO]: "Dental PPO",
  [CoverageTypeEnum.DENTAL_HMO]: "Dental HMO",
  [CoverageTypeEnum.VISION]: "Vision",
  [CoverageTypeEnum.STD]: "Short Term Disability",
  [CoverageTypeEnum.LTD]: "Long Term Disability",
  [CoverageTypeEnum.BASIC_LIFE_ADND]: "Basic Life/AD&D",
  [CoverageTypeEnum.VOLUNTARY_LIFE_ADND]: "Voluntary Life/AD&D",
  [CoverageTypeEnum.ACCIDENT]: "Accident Insurance",
  [CoverageTypeEnum.CRITICAL_ILLNESS]: "Critical Illness Insurance",
  [CoverageTypeEnum.HOSPITAL_INDEMNITY]: "Hospital Indemnity Insurance",
  [CoverageTypeEnum.CANCER]: "Cancer Insurance",
  [CoverageTypeEnum.FMLA]: "Family Medical Leave Administration",
  [CoverageTypeEnum.EAP]: "Employee Assistance Program",
  [CoverageTypeEnum.INDIVIDUAL_DISABILITY_INSURANCE]:
    "Individual Disability Insurance",
  [CoverageTypeEnum.WHOLE_LIFE]: "Whole Life",
  [DocumentLibraryCoverageType.STATE_DISABILITY_AND_LEAVES]:
    "State Disability & Leaves",
};

export const EXCLUDED_COVERAGE_TYPES: CoverageTypeEnum[] = [
  CoverageTypeEnum.CANCER,
  CoverageTypeEnum.INDIVIDUAL_DISABILITY_INSURANCE,
  CoverageTypeEnum.WHOLE_LIFE,
  CoverageTypeEnum.FMLA,
  CoverageTypeEnum.EAP,
];

export const DOCUMENT_TYPE_VALUES: DocumentType[] = [
  "summary",
  "summary_rates",
  "certificate",
  "certificate_rates",
  "rates",
  "unknown",
];

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  summary_rates: "Summary/Rates",
  summary: "Summary",
  certificate: "Cert",
  certificate_rates: "Cert/Rates",
  rates: "Rates",
  unknown: "Unknown",
};

export const PRIORITY_DOC_TYPES: DocumentType[][] = [
  ["certificate", "certificate_rates"],
  ["summary", "summary_rates"],
];

export const BROKER_DOCUMENT_TYPES = {
  CENSUS: "Census",
  CURRENT_PLAN_DOCUMENT: "Current Plan Document",
  RENEWAL_RATE: "Renewal Rate",
  PROVIDER_UTILIZATION_REPORT: "Provider Utilization Report",
  DENTALPPO: "Dental PPO Average Discount Exhibit",
  MONTHLY_PREMIUM_VS_CLAIMS_EXHIBIT: "Monthly Premium vs Claims Exhibit",
  LTD_CLAIMS_LISTING: "Open/Closed LTD Claims Listing",
  LTD_RESERVES_EXHIBIT: "LTD Reserves Exhibit",
  CURRENT_RATES: "Current Rates",
  INCIDENT_REPORT: "Incident Report",
  WAIVER: "Waiver",
};

export const BROKER_DOCUMENT_TYPE_LABELS: Record<string, string> =
  Object.fromEntries(
    Object.entries(BROKER_DOCUMENT_TYPES).map(([key, label]) => [
      key.toLowerCase(),
      label,
    ]),
  );

/**
 * How many minutes a document may show Processing before its card offers
 * Remove File and Process with AI again, unless the
 * ai-docs-stuck-processing-minutes feature sets another value. A job still
 * pending this long has most likely lost its workflow, and nothing else would
 * ever move it on. Some real jobs run longer, so a retry here can overlap one
 * that would have finished.
 */
export const DEFAULT_STUCK_PROCESSING_MINUTES = 5;
